import React, { useState, useMemo, useContext } from 'react';

import _ from 'lodash';

import styled from 'styled-components';

import { useLayoutContext } from '@contexts/LayoutContext';
import { useStateValue } from '@contexts/StateProvider';
import { UserContext } from '@contexts/UserContext';

import ContactsFilterDrawer from '@components/ContactsSelector/Common/ContactsFilterDrawer';
import {
    useContactsSelector,
    useContactsSelectorDispatch,
} from '@components/ContactsSelector/Common/ContactsSelectorContext';
import { MOBILE_FILTERS_NESTED_ZINDEX } from '@components/Filters/FilterForm/FilterFormUI';
import { NwButton, NwRemoveButton, NwEditButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import NwDividerVertical from '@components/Gui/NwDividerVertical';
import NwDrawer from '@components/Gui/NwDrawer';

import ContactsFilterSetNameDrawer from './ContactsFilterSetNameDrawer';
import ContactsFiltersSections from './ContactsFiltersSections';
import ContactsSelectedFilters from './ContactsSelectedFilters';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

//.filter-actions e' condivisa via FiltersWrapper (FilterSidebarStyle.js): stessa riga di
//bottoni per "actions on current X" nella tab folders
const Wrapper = styled.div`
    width: 100%;
    display: block;
`;

const ContactsFiltersSmartListsForm = ({
    navigation,
    //onSave
}) => {
    const [{ areaContext }] = useStateValue();
    const { breakpoints } = useLayoutContext();

    const [openAddDrawer, setOpenAddDrawer] = useState(false);
    const [openEditDrawer, setOpenEditDrawer] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const { global, selection, layout, context } = useContactsSelector();
    const { filtersSections, sets } = global;
    const { listType, activeSet, activeSetFixed, activeSetName, filters } = selection;
    const { currentPackage, currentEvent, scope } = context;
    const cs_dispatch = useContactsSelectorDispatch();

    const { currentUser } = useContext(UserContext);

    const currentMailingList = scope === 'mailing-list';

    const workingSetUnsaved = useMemo(() => {
        if (listType === 'smart-lists') {
            const activeSetObj = sets.find((set) => set.id === activeSet);
            return activeSetObj && activeSetObj.unsaved;
        }
        return false;
    }, [activeSet, listType, sets]);

    const sectionName = useMemo(() => {
        if (filters.openSection) {
            const section = filtersSections.find((section) => section.areaID === filters.openSection);
            if (section) {
                return section.areaName;
            }
        }
        return '';
    }, [filters.openSection]);

    const closeFilterSection = () => {
        cs_dispatch({ type: 'CloseFiltersSection' });
    };

    const revertChanges = () => {
        cs_dispatch({ type: 'RevertChanges' });
    };

    const onUpdateSetFilters = () => {
        setIsSaving(true);
        cs_dispatch({
            type: 'SaveSetFilters',
        });
        setIsSaving(false);
        handleCloseEditDrawer();
        //onSave()
    };

    const onDeleteSet = () => {
        cs_dispatch({
            type: 'DeleteSet',
            area: areaContext.area,
            setID: activeSet,
        });
        handleCloseAddDrawer();
    };

    const handleOpenAddListDrawer = () => {
        setOpenAddDrawer(true);
    };

    const handleCloseAddDrawer = () => {
        setOpenAddDrawer(false);
    };

    const handleOpenEditListDrawer = () => {
        setOpenEditDrawer(true);
    };

    const handleCloseEditDrawer = () => {
        setOpenEditDrawer(false);
    };

    const drawerOnDrawer = currentPackage || currentEvent || currentMailingList || layout.drawer;
    // Sotto i 1024px la sidebar vive dentro il pannello filtri a tutta pagina:
    // i drawer annidati devono aprirsi a tutta larghezza e sopra al pannello.
    const inMobileFiltersPanel = breakpoints < 3;

    let fromLeft = 0;
    if (!drawerOnDrawer && !inMobileFiltersPanel) {
        if (breakpoints < 4) {
            fromLeft = 200;
        } else {
            fromLeft = 300;
        }
        if (layout.leftMargin) {
            fromLeft += layout.leftMargin;
        }
    }
    let placement = drawerOnDrawer || inMobileFiltersPanel ? 'right' : 'left';
    let zIndex = inMobileFiltersPanel ? MOBILE_FILTERS_NESTED_ZINDEX : drawerOnDrawer ? 1041 : 5;
    let fromTop = drawerOnDrawer || inMobileFiltersPanel ? 0 : navigation ? 110 : 60;

    let fromTop2 = drawerOnDrawer || inMobileFiltersPanel ? 0 : navigation ? 95 : 60;

    // check if there's a departments filter in any area and at least one of the values in array is not in current context departments
    const hasDepartmentsFilter = filters.active.some((filter) => filter.name === 'Departments');
    if (hasDepartmentsFilter) {
        const departmentsFilterValues = filters.active.find((filter) => filter.name === 'Departments').value;
        const notInCurrentContextDepartments = departmentsFilterValues.filter(
            (value) => !currentUser.ActiveContext.activeDepartments.includes(value)
        );
        if (notInCurrentContextDepartments.length > 0) {
            return (
                <Wrapper>
                    <NwDivider>warning</NwDivider>
                    <div>
                        This search is using departments that are not in your current context. You can't edit this
                        search.
                    </div>
                </Wrapper>
            );
        }
    }

    return (
        <Wrapper>
            <NwDivider>active filters</NwDivider>
            <ContactsSelectedFilters />
            {activeSetName && (!activeSetFixed || workingSetUnsaved) && (
                <>
                    <NwDivider lessMargin>actions on current search</NwDivider>
                    <div className="filter-actions">
                        {workingSetUnsaved ? (
                            <>
                                <NwButton
                                    size="small"
                                    ghost
                                    icon={byPrefixAndName.fass['undo']}
                                    label="reset"
                                    onClick={revertChanges}
                                />
                                <NwDividerVertical />
                                {!activeSetFixed && (
                                    <>
                                        <NwButton
                                            size="small"
                                            ghost
                                            icon={byPrefixAndName.fass['save']}
                                            onClick={onUpdateSetFilters}
                                            label="save"
                                            loadingLabel="saving..."
                                        />
                                        <NwDividerVertical />
                                    </>
                                )}
                                <NwButton
                                    size="small"
                                    icon={byPrefixAndName.fasl['floppy-disk-circle-arrow-right']}
                                    label="save as..."
                                    ghost
                                    onClick={handleOpenAddListDrawer}
                                />
                            </>
                        ) : (
                            !activeSetFixed && (
                                <>
                                    <NwEditButton
                                        size="small"
                                        ghost
                                        onClick={handleOpenEditListDrawer}
                                        label="rename"
                                        loadingLabel="saving..."
                                    />
                                    <NwDividerVertical />
                                    <NwRemoveButton
                                        size="small"
                                        ghost
                                        onClick={onDeleteSet}
                                        label="delete"
                                        loadingLabel="deleting..."
                                    />
                                </>
                            )
                        )}
                    </div>
                </>
            )}
            <ContactsFiltersSections />
            {filters.openSection && (
                <NwDrawer
                    fromLeft={fromLeft}
                    fromTop={fromTop}
                    zIndex={zIndex}
                    placement={placement}
                    className={inMobileFiltersPanel ? undefined : 'max-width-800'}
                    width={inMobileFiltersPanel ? '100vw' : 'calc(100vw - 340px)'}
                    onClose={closeFilterSection}
                    title={sectionName}
                >
                    <ContactsFilterDrawer />
                </NwDrawer>
            )}
            {openAddDrawer && (
                <ContactsFilterSetNameDrawer
                    fromLeft={fromLeft}
                    fromTop={fromTop2}
                    zIndex={zIndex}
                    placement={placement}
                    onClose={handleCloseAddDrawer}
                    isSaving={isSaving}
                    action="AddNew"
                />
            )}
            {openEditDrawer && (
                <ContactsFilterSetNameDrawer
                    fromLeft={fromLeft}
                    fromTop={fromTop2}
                    zIndex={zIndex}
                    placement={placement}
                    onClose={handleCloseEditDrawer}
                    isSaving={isSaving}
                    action="Rename"
                />
            )}
        </Wrapper>
    );
};

export default ContactsFiltersSmartListsForm;
